import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Clock, ArrowUpRight, Navigation, Users, PartyPopper, Clapperboard } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { INSTAGRAM_URL } from "@/components/Header";

const SECTION_PAD = "py-16 sm:py-20";
const SECTION_H2 = "font-display font-black tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-[2.6rem] text-ink";
const BTN_PRIMARY = "inline-flex items-center justify-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors";
const BTN_OUTLINE = "inline-flex items-center justify-center gap-3 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors";

export default function Cafe() {
  const [photos, setPhotos] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/content/cafe").then((r) => setPhotos(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const address = settings.cafe_address || "3877 Holland Road, Suite 509, Virginia Beach, VA 23452";
  const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  const gallery = photos.slice(0, 4);

  return (
    <div data-testid="cafe-page">
      {/* HERO — bright water brand moment */}
      <section className="relative overflow-hidden" data-testid="cafe-hero">
        <img src="/assets/shop-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/75 to-white/30 pointer-events-none" aria-hidden="true" />
        <div className="relative container-site py-16 sm:py-20 lg:py-24">
          <Reveal className="max-w-2xl">
            <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              <span className="block text-ink">Come for the Food.</span>
              <span className="block text-pool">Stay for the Vibe.</span>
            </h1>
            <p className="mt-6 text-ink/70 leading-relaxed max-w-md text-base" data-testid="cafe-hero-sub">
              Brunch. Celebrity experiences. Good vibes — all served poolside.
            </p>
            <div className="mt-8">
              <a href="#more-than-a-cafe" data-testid="cafe-hero-explore" className={BTN_PRIMARY}>
                Explore the Café <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STEP INSIDE — vertical tour video + More Than a Café */}
      <section id="more-than-a-cafe" className={`relative overflow-hidden bg-white ${SECTION_PAD}`} data-testid="cafe-tour">
        <div className="absolute -right-24 -top-16 h-80 w-80 rounded-full bg-pool-light/50 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="container-site relative">
          <Reveal className="max-w-xl mb-10">
            <p className="label-eyebrow mb-5">Step Inside</p>
            <h2 className={SECTION_H2} data-testid="cafe-tour-headline">
              A Look Inside <span className="text-pool">the Café.</span>
            </h2>
          </Reveal>
          <div className="grid lg:grid-cols-[38%_62%] gap-10 lg:gap-14 items-center">
            <Reveal>
              <div className="relative max-w-xs mx-auto lg:mx-0 w-full">
                <div className="absolute -inset-3 rounded-3xl bg-pool/10 lg:-translate-x-4 lg:translate-y-4" aria-hidden="true" />
                <div className="relative aspect-[9/16] overflow-hidden rounded-2xl shadow-xl">
                  <video
                    src="/assets/cafe-tour.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    className="absolute inset-0 h-full w-full object-cover bg-ink"
                    data-testid="cafe-tour-video"
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="font-script text-4xl sm:text-5xl text-pool leading-none">More Than a Café.</p>
              <h3 className="mt-2 font-display font-black tracking-tight leading-[1.02] text-2xl sm:text-3xl lg:text-4xl text-ink">
                It's an Experience.
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed text-base max-w-md">
                From celebrity brunches and private celebrations to live tapings and special events, Poolside Cooking
                Café brings the energy of the show to life.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/events#book-your-event" data-testid="tour-book-cta" className={BTN_PRIMARY}>
                  Book an Event <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#cafe-gallery" data-testid="tour-explore-cta" className={BTN_OUTLINE}>
                  Explore the Café <ArrowRight className="h-4 w-4" />
                </a>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md" data-testid="tour-features">
                {[
                  { icon: Users, label: "Celebrity Brunches" },
                  { icon: PartyPopper, label: "Private Events" },
                  { icon: Clapperboard, label: "Live Experiences" },
                ].map((f) => (
                  <div key={f.label} className="text-center" data-testid={`tour-feature-${f.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="mx-auto h-11 w-11 rounded-full bg-pool-light/60 border border-pool/25 flex items-center justify-center">
                      <f.icon className="h-5 w-5 text-pool-deep" />
                    </div>
                    <p className="mt-2.5 text-[9px] sm:text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 leading-tight">{f.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-6" id="cafe-gallery" data-testid="cafe-gallery-row">
            {[
              { src: "/assets/cafe-storefront.jpg", alt: "Poolside Cooking Café storefront" },
              { src: "/assets/cafe-sliders.jpg", alt: "Signature sliders at the Poolside Cooking Café" },
              { src: "/assets/cafe-event.jpg", alt: "Event setup at the Poolside Cooking Café" },
            ].map((p, i) => (
              <Reveal key={p.src} delay={i * 0.07}>
                <div className="relative aspect-[4/3] overflow-hidden rounded-xl shadow-md" data-testid={`cafe-gallery-${i}`}>
                  <img src={p.src} alt={p.alt} loading="lazy" className="img-zoom absolute inset-0 h-full w-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* A TASTE OF POOLSIDE */}
      <section className={`bg-cream ${SECTION_PAD}`} data-testid="cafe-taste">
        <div className="container-site grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="label-eyebrow mb-5">From the Kitchen</p>
            <h2 className={SECTION_H2}>
              A Taste of <span className="text-pool">Poolside</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed text-base max-w-md">
              Fresh flavors, bold bites and brunch favorites — always served with good vibes.
            </p>
            {settings.cafe_menu_link && (
              <a
                href={settings.cafe_menu_link}
                target="_blank"
                rel="noreferrer"
                data-testid="view-menu-cta"
                className={`${BTN_PRIMARY} mt-8`}
              >
                View Menu <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-3 gap-3 sm:gap-4" data-testid="cafe-collage">
              {gallery.map((p, i) => (
                <div
                  key={p.id}
                  className={`relative overflow-hidden rounded-xl shadow-md ${i === 0 ? "col-span-3 aspect-[16/9]" : "aspect-square"}`}
                >
                  <img src={imgSrc(p.image)} alt={p.title} loading="lazy" className="img-zoom absolute inset-0 h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* VISIT US */}
      <section className={`bg-white overflow-hidden ${SECTION_PAD}`} data-testid="cafe-visit">
        <div className="container-site grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="label-eyebrow mb-5">Visit Us</p>
            <h2 className={SECTION_H2}>
              Come See <span className="text-pool">Us.</span>
            </h2>
            <div className="mt-7 space-y-3.5 text-sm sm:text-base text-slate-600">
              <p className="font-display font-extrabold text-base sm:text-lg text-slate-900">Poolside Cooking Café</p>
              <p className="flex items-start gap-3" data-testid="cafe-address">
                <MapPin className="h-4 w-4 text-pool shrink-0 mt-1" />
                <span>
                  3877 Holland Road, Suite 509
                  <br />
                  Virginia Beach, VA 23452
                </span>
              </p>
              {settings.cafe_phone && (
                <p className="flex items-center gap-3"><Phone className="h-4 w-4 text-pool shrink-0" /> {settings.cafe_phone}</p>
              )}
              <p className="flex items-start gap-3" data-testid="cafe-hours">
                <Clock className="h-4 w-4 text-pool shrink-0 mt-1" />
                {settings.cafe_hours || "Hours coming soon"}
              </p>
              {settings.cafe_booking_note && (
                <p
                  className="!mt-5 inline-block bg-pool-light/60 border border-pool/30 text-pool-deep text-xs font-bold tracking-wide px-4 py-2 rounded-full"
                  data-testid="cafe-booking-note"
                >
                  {settings.cafe_booking_note}
                </p>
              )}
              <p className="text-slate-500 pt-1 text-sm">
                Follow{" "}
                <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="text-pool font-semibold hover:underline" data-testid="cafe-instagram">
                  @poolsidecookingshow
                </a>{" "}
                for the latest.
              </p>
            </div>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/events" data-testid="cafe-book-event-cta" className={BTN_PRIMARY}>
                Book an Event <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={directionsUrl} target="_blank" rel="noreferrer" data-testid="cafe-directions-cta" className={BTN_OUTLINE}>
                Get Directions <Navigation className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-pool/10 lg:translate-x-4 lg:translate-y-4" aria-hidden="true" />
              <div className="relative aspect-[4/3] max-h-[440px] overflow-hidden rounded-2xl shadow-lg">
                <img
                  src="/assets/cafe-storefront.jpg"
                  alt="Poolside Cooking Café storefront"
                  loading="lazy"
                  className="img-zoom h-full w-full object-cover"
                  data-testid="cafe-visit-image"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
